import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { BulkImportModalView, BulkImportModalViewProps } from './BulkImportModal.view';
import { idGenerator } from '../../../helpers/idGenerator';
import { ModalsContext } from '../../../providers/ModalsProvider';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { useCallbackPrompt } from '../../../hooks/useCallbackPrompt';
import { useDidMount } from '../../../hooks/useDidMount';
import { useKeypress } from '../../../hooks/useKeypress';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import { FieldValues, SubmitErrorHandler } from 'react-hook-form';
import { BulkActionsContext } from '../states/BulkActionsProvider';
import { PostEditorContext } from '../states/PostEditorProvider';
import { LinkClickTracking } from '../../../models/graphql';

export type BulkImportModalControllerProps = {
  setBulkImportModal: BulkImportModalViewProps['setBulkImportModal'];
};
export const BulkImportModalController = (props: BulkImportModalControllerProps): JSX.Element => {
  const { setBulkImportModal } = props;
  const { state, linksMetadata } = useContext(BulkActionsContext);
  const { dispatch: dispatchPostEditor, profiles: availableProfiles, editors } = useContext(PostEditorContext);
  const [editorsRef] = editors;

  const ref = useRef(null);
  const modalId = useMemo(() => idGenerator('modal'), []);
  const [modalsRef, modalsUpdater] = useContext(ModalsContext);
  const breakpoint = useBreakpoints();

  const onCloseAttempt = () => {
    const lastModalRef = modalsRef.current[modalsRef.current.length - 1];
    if (lastModalRef.id === modalId) {
      setBulkImportModal(false);
    }
  };

  const { showPrompt, confirmNavigation, cancelNavigation } = useCallbackPrompt(modalId, onCloseAttempt);
  const navigationRef = useRef({ confirmNavigation, cancelNavigation });

  useDidMount(() => {
    if (modalId && !modalsRef.current.filter(item => item.id === modalId).length) {
      modalsUpdater({ type: 'add', value: modalId })(modalsRef);
    }
    return () => {
      modalsUpdater({ type: 'remove', value: modalId })(modalsRef);
    };
  });

  useEffect(() => {
    const lastModalRef = modalsRef.current[modalsRef.current.length - 1];
    if (showPrompt && lastModalRef.id === modalId) {
      navigationRef.current.confirmNavigation();
    }
  }, [showPrompt, breakpoint, modalsRef, modalId]);

  const onEscapeHandler = (e?: KeyboardEvent) => {
    // If True or not set, we close
    const lastModalRef = modalsRef.current[modalsRef.current.length - 1];
    if (lastModalRef.id === modalId) {
      e && e.stopImmediatePropagation();
      setBulkImportModal(false);
    }
  };

  const submitHandler: SubmitHandler<FieldValues> = () => {
    dispatchPostEditor({
      type: 'bulkImportSet',
      payload: {
        availableProfiles,
        postStatus:
          state.targeting.checkboxes.postStatus && state.targeting.content.postStatus
            ? state.targeting.content.postStatus
            : undefined,
        profiles: state.targeting.checkboxes.profiles ? state.targeting.content.profiles : undefined,
        category: state.targeting.checkboxes.category ? state.targeting.content.category : undefined,
        postFeedType: state.targeting.checkboxes.instagramPostFeedType
          ? state.targeting.content.instagramPostFeedType
          : undefined,
        publishMethod: state.targeting.checkboxes.instagramPublishMethod
          ? state.targeting.content.instagramPublishMethod
          : undefined,
        linkDestination: state.linkSettings.checkboxes.linkDestination
          ? state.linkSettings.content.linkDestination
          : undefined,
        keepExistingUtm: state.linkSettings.checkboxes.keepExistingUtm
          ? state.linkSettings.content.keepExistingUtm
          : undefined,
        utmTracking: state.linkSettings.checkboxes.addUtmCampaignTracking
          ? state.linkSettings.content.addUtmCampaignTracking
            ? state.linkSettings.content.utmSettings
            : null
          : undefined,
        useBitly: state.linkSettings.checkboxes.bitly
          ? state.linkSettings.content.bitly === LinkClickTracking.BITLY
          : undefined,
        queuePosition: state.scheduling.checkboxes.positionInQueue
          ? state.scheduling.content.positionInQueue
          : undefined,
        evergreen: state.scheduling.checkboxes.evergreenRecycle ? state.scheduling.content.evergreenRecycle : undefined,
        links: linksMetadata,
        editors: editorsRef
      }
    });
    setBulkImportModal(false);
  };

  const errorHandler: SubmitErrorHandler<FieldValues> = (_errors, _event) => {
    // TODO Do we need something here?
  };

  useKeypress('Escape', onEscapeHandler);

  return (
    <BulkImportModalView
      onSubmit={submitHandler}
      onError={errorHandler}
      setBulkImportModal={setBulkImportModal}
      ref={ref}
    />
  );
};
